import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAvailableSerialsApi,
  getCurrentStockApi,
  getReplaceVoucherNoApi,
  getUnavailableSerialsApi,
  replaceRecordWithDetailsApi,
  salesVoucherApi,
  salesRecordWithDetailsApi,
  getSalesReplaceVouchersApi,
  getSalesReturnVouchersApi,
  getSalesReturnedItemsApi,
  getCustomerSpecialPriceApi,
} from "../../apiservice/sales";
import {
  getSundryDebitorBalanceApi,
  getItemBySearchApi,
} from "../../apiservice/accounts";
import {
  getAccountBySeachApi,
  getWarehouseApi,
  getAccountByTypeApi,
} from "../../apiservice/setting";
import { createProductReplacePayload } from "../../utils/productReplace";
import { fetchData, postData } from "../../services/apis";
import { setFormField } from "./productReplaceSlice";
import { FRONTEND_APP_URL } from "../../environment";
import { selectSelectedVoucherDetails } from "./productReplaceSelectors";

export const fetchReplaceVoucherNumber = createAsyncThunk(
  "productReplace/fetchReplaceVoucherNumber",
  async () => {
    const res = await getReplaceVoucherNoApi();
    return res.data;
  }
);

export const createProductReplace = createAsyncThunk(
  "productReplace/createProductReplace",
  async ({ payload, isUpdate }, { getState, rejectWithValue }) => {
    const url = isUpdate ? "/sales/update-replace" : "/sales/create-replace";
    try {
      const state = getState().productReplace;
      const transformed = state.footerValues?.payCart?.map(
        ({ from_acc_id, from_acc_name, ...rest }) => ({
          ...rest,
          to_acc_id: from_acc_id,
          to_acc_name: from_acc_name,
        })
      );

      const payload = createProductReplacePayload({
        givenItemsList: state.givenItemsList,
        returnItemsList: state.returnItemsList,
        customerInfo: state.customerInfo,
        footerValues: state.footerValues,
        voucherNumber: state.voucherNumber,
        selectedTaxAcc: state.selectedTaxAcc,
        selectedVoucherDetails: state.selectedVoucherDetails,
        // payCart: state.footerValues.payCart || [],
        payCart: transformed || [],
        payServiceCart: state.payServiceCart || [],
      });

      const res = await postData(url, payload);

      return res.data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// export const fetchCustomersList = createAsyncThunk(
//   "productReplace/fetchCustomersList",
//   async (searchText = "", { dispatch }) => {
//     const res = await await(getAccountBySeachApi({searchText}));
//     dispatch(setFormField({ field: "customersList", value: res.data || [] }));
//     return res.data;
//   }
// );
export const fetchCustomersList = createAsyncThunk(
  "productReplace/fetchCustomersList",
  async (search, { getState }) => {
    const state = getState();
    const authInfo = state.auth.auth_info;
    let data = [];
    if (authInfo.subscription === "SuperERP") {
      data = ["Customer", "Patient"];
    } else if (authInfo.branch_company_type === "Pharmacy") {
      data = ["Customer", "Patient"];
    } else {
      data = ["Customer"];
    }
    const res = await getAccountBySeachApi({ query: search, type: "debitor" });
    return res.data || [];
  }
);

export const fetchTaxAccs = createAsyncThunk(
  "productReplace/fetchTaxAccs",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAccountByTypeApi({ acc_type_id: "dutie_&_tax" });
      return res.data?.length > 0 ? res.data[0] : null;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchSundryDebitorBalance = createAsyncThunk(
  "productReplace/fetchSundryDebitorBalance",
  async (_, { getState }) => {
    const state = getState();
    const customerId = state.productReplace.customerInfo?.acc_id || 0;
    const res = await getSundryDebitorBalanceApi({ customerId });
    return res.data || [];
  }
);

export const fetchSalesVouchers = createAsyncThunk(
  "salesReturn/fetchSalesVouchers",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const selectedCustomer = state.productReplace.customerInfo;
      const [vouchersRes, replaceRes, returnRes] = await Promise.all([
        salesVoucherApi({
          customerId: selectedCustomer != null ? selectedCustomer.acc_id : null,
        }),
      ]);
      const usedSaleIds = new Set(
        [...returnRes.data, ...replaceRes.data].map((x) => x.sale_id)
      );
      const filtered = vouchersRes.data.filter(
        (v) => !usedSaleIds.has(v.sale_id)
      );
      return filtered;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchFilteredVouchers = createAsyncThunk(
  "productReplace/fetchFilteredVouchers",
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const selectedCustomer = state.productReplace.customerInfo;

    try {
      const salesVouchersRes = await salesVoucherApi({
        customerId: selectedCustomer?.acc_id ?? null,
      });

      const salesVouchers = salesVouchersRes.data || [];

      const filteredVouchers = [];

      for (const voucher of salesVouchers) {
        // fetch details & returned items
        const [salesDetailsRes, returnedItemsRes] = await Promise.all([
          salesRecordWithDetailsApi(voucher.sale_id),
          getSalesReturnedItemsApi({ sale_id: voucher.sale_id }),
        ]);

        const saleDetails = salesDetailsRes.data[0]?.details || [];
        const returnedItems = returnedItemsRes.data || [];

        const hasAvailableItems = saleDetails.some((item) => {
          const returned = returnedItems.find(
            (r) => r.item_id === item.item_id
          );
          const availableQty =
            item.sale_qty - (returned?.total_return_qty || 0);
          return availableQty > 0;
        });

        if (hasAvailableItems) {
          filteredVouchers.push(voucher);
        }
      }
      return filteredVouchers;
    } catch (error) {
      console.error("Error fetching vouchers", error);
      return rejectWithValue(error.message);
    }
  }
);

// Get details of a selected sale
export const fetchSalesVoucherDetails = createAsyncThunk(
  "productReplace/fetchSalesVoucherDetails",
  async (saleId, { rejectWithValue }) => {
    try {
      const [salesDetails, returnedItems] = await Promise.all([
        salesRecordWithDetailsApi(saleId),
        getSalesReturnedItemsApi({ sale_id: saleId }),
      ]);
      return {
        salesDetails: salesDetails.data,
        returnedItems: returnedItems.data,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// export const fetchFilteredItems = createAsyncThunk(
//   "productReplace/fetchFilteredItems",
//   async ({ customerId }, { rejectWithValue }) => {
//     try {
//       const res1 = await salesVoucherApi({ customerId });
//       const res2 = await getSalesReplaceVouchersApi({});
//       const res3 = await getSalesReturnVouchersApi({});

//       if ([res1, res2, res3].some(r => r === "Error: Request failed with status code 401")) {
//         sessionStorage.clear();
//         window.location.href = FRONTEND_APP_URL;
//         return rejectWithValue("Unauthorized");
//       }

//       // Filtering
//       const replaceSaleIds = res2.data.map(v => v.sale_id);
//       const returnSaleIds = res3.data.map(v => v.sale_id);
//       const filteredVouchers = res1.data.filter(
//         v => !replaceSaleIds.includes(v.sale_id) && !returnSaleIds.includes(v.sale_id)
//       );

//       return { vouchers: filteredVouchers }; // minimal shaping here
//     } catch (err) {
//       return rejectWithValue(err.message);
//     }
//   }
// );

export const fetchFilteredItems = createAsyncThunk(
  "productReplace/fetchFilteredItems",
  async (saleId, { rejectWithValue }) => {
    try {
      const payload = { sale_id: saleId };

      const [returnedItemsRes, saleDetailsRes] = await Promise.all([
        getSalesReturnedItemsApi(payload),
        salesRecordWithDetailsApi(payload),
      ]);

      const returnItems = returnedItemsRes.data;
      const saleData = saleDetailsRes.data[0];
      const saleDetails = saleData.details;

      // Build items array
      let total_item_qty = 0;
      const items = saleDetails.map((item) => {
        total_item_qty += item.sale_qty;

        let serials =
          item.is_serial === "yes"
            ? item.serials.split(",").map((slNo) => ({ serial_number: slNo }))
            : [];

        const unittotal =
          parseFloat((item.item_return_unit_price || 0).toFixed(2)) *
          item.item_qty;

        return {
          group_name: item.group_name,
          category_name: item.category_name,
          warehouse_id: item.warehouse_id,
          warehouse_name: item.warehouse_name,
          item_id: item.item_id,
          item_name: item.item_name,
          display_text: item.display_text,
          category_id: item.category_id,
          group_id: item.group_id,
          is_serial: item.is_serial,
          item_qty: 0,
          return_item_rate: item.item_return_unit_price,
          return_rate: item.item_return_unit_price,
          item_discount: item.item_discount,
          item_discount_per: item.item_discount_per,
          discount_acc_id: item.discount_acc_id,
          discount_acc_name: item.discount_acc_name,
          item_tax: item.item_tax,
          item_tax_per: item.item_tax_per,
          tax_acc_id: item.tax_acc_id,
          tax_acc_name: item.tax_acc_name,
          serials,
          return_item_qty: item.item_qty,
          return_qty: item.item_qty,
          return_item_total: unittotal,
          return_retail_qty: item.item_qty,
          retail_qty: 0,
          sale_r_qty: 0,
          sale_r_rate: item.sale_rate,
          sold_qty: item.sale_qty,
          qty_display:
            item.item_qty +
            " " +
            item.unit_symbol +
            (item.conversion > 1
              ? ", " + item.retail_qty + " " + item.base_unit_name
              : ""),
          unit_symbol: item.unit_symbol,
          base_unit_name: item.base_unit_name,
          unit_name: item.unit_name,
          conversion: item.conversion,
          purchase_average_rate: item.purchase_average_rate,
          batch_id: item.batch_id,
          units: item.units,
          per_symbol: item.unit_symbol,
          per_conversion: item.per_conversion,
          per_unit_id: item.per_unit_id,
          per_unit_symbol: item.per_unit_symbol,
        };
      });

      // Merge with returned items
      const merged = items.map((item) => {
        const match = returnItems.find((r) => r.item_id === item.item_id);
        const available_qty = match
          ? item.sold_qty - match.total_return_qty
          : item.sold_qty;

        return {
          ...item,
          ...(match || {}),
          available_qty,
        };
      });

      return {
        customerInfo: {
          acc_id: saleData.acc_id,
          acc_name: saleData.acc_name,
          display_text: saleData.acc_name,
          institution_name: saleData.institution_name,
          contact_no: saleData.contact_no,
          address: saleData.address,
          country_code: saleData.country_code,
        },
        items: merged,
        total_item_qty,
      };
    } catch (error) {
      console.error("Error fetching filtered items", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchReplaceRecordWithDetail1 = createAsyncThunk(
  "productReplace/fetchReplaceRecordWithDetail1",
  async (replace_id, { rejectWithValue }) => {
    try {
      const res = await replaceRecordWithDetailsApi({ replace_id });

      if (!res.data || res.data.length === 0) {
        return rejectWithValue("No record found");
      }

      const data = res.data[0];
      const dataMIDetails = data.miDetails || [];
      const dataMCIDetails = data.mciDetails || [];

      // map given items
      const givenItemsList = dataMIDetails.map((item, idx) => {
        const serials =
          item.is_serial === "yes" && item.serials
            ? item.serials.split(",").map((sl) => ({ serial_number: sl }))
            : [];
        return {
          key: `g-${idx + 1}`,
          item_id: item.item_id,
          product: item.item_name,
          quantity: Number(item.item_qty || 0),
          rate: Number(item.item_rate || 0),
          tax_per: Number(item.item_tax_per || 0),
          warehouse_id: item.warehouse_id,
          warehouse_name: item.warehouse_name,
          per_unit_id: item.per_unit_id || 0,
          per_unit_symbol: item.per_unit_symbol,
          unit_symbol: item.unit_symbol,
          base_unit_name: item.base_unit_name,
          conversion: Number(item.conversion || 1),
          serials,
          given_qty: Number(item.given_qty || 0),
          given_rate: Number(item.given_rate || 0),
          item_total: Number(item.item_total || 0),
          item_percentage: Number(item.item_percentage || 100),
          units: item.units || [],
        };
      });

      // map return items
      const returnItemsList = dataMCIDetails.map((item, idx) => {
        const serials =
          item.is_serial === "yes" && item.serials
            ? item.serials.split(",").map((sl) => ({ serial_number: sl }))
            : [];
        return {
          key: `r-${idx + 1}`,
          item_id: item.item_id,
          product: item.item_name,
          quantity: Number(item.return_qty || 0),
          rate: Number(item.return_rate || 0),
          vat_per: Number(item.item_tax_per || 0),
          warehouse_id: item.warehouse_id,
          warehouse_name: item.warehouse_name,
          per_unit_id: item.per_unit_id || 0,
          per_unit_symbol: item.per_unit_symbol,
          unit_symbol: item.unit_symbol,
          base_unit_name: item.base_unit_name,
          conversion: Number(item.conversion || 1),
          serials,
          return_item_total: Number(item.return_item_total || 0),
          return_retail_qty: Number(item.return_retail_qty || 0),
          batch_id: item.batch_id,
          units: item.units || [],
        };
      });

      return {
        customerInfo: {
          acc_id: data.acc_id,
          acc_name: data.acc_name,
          display_text: data.acc_name,
          institution_name: data.institution_name,
          contact_no: data.contact_no,
          country_code: data.country_code,
          address: data.address,
        },
        voucherNumber: data.voucher_no,
        givenItemsList,
        returnItemsList,
        footerValues: {
          narration: data.narration,
          labour_cost: data.labour_cost,
          others_cost: data.others_cost,
        },
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchReplaceRecordWithDetail = createAsyncThunk(
  "productReplace/fetchReplaceRecordWithDetail",
  async (replace_id, { rejectWithValue }) => {
    try {
      const res = await replaceRecordWithDetailsApi({ replace_id });

      if (!res.data || res.data.length === 0) {
        return rejectWithValue("No record found");
      }

      const data = res.data[0];
      const dataMIDetails = data.miDetails || [];
      const dataMCIDetails = data.mciDetails || [];

      // Map Given Items
      const givenItemsList = dataMIDetails.map((item, idx) => ({
        key: `g-${idx + 1}`,
        product: item.item_name, // ✅ Will show in table
        item_id: item.item_id,
        quantity: Number(item.item_qty || 0),
        rate: Number(item.item_rate || 0),
        tax_per: Number(item.item_tax_per || 0),
        warehouse_id: item.warehouse_id,
        per_unit_id: item.per_unit_id || 0,
        per_unit_symbol: item.per_unit_symbol,
        unit_symbol: item.unit_symbol,
        conversion: Number(item.conversion || 1),
        serials: item.serials ? item.serials.split(",") : [],
        item_total: Number(item.item_total || 0),
        units: item.units || [],
      }));

      // Map Return Items
      const returnItemsList = dataMCIDetails.map((item, idx) => ({
        key: `r-${idx + 1}`,
        product: item.item_name,
        item_id: item.item_id,
        quantity: Number(item.return_qty || 0),
        rate: Number(item.return_rate || 0),
        tax_per: Number(item.item_tax_per || 0),
        unit_symbol: item.unit_symbol,
        serials: item.serials ? item.serials.split(",") : [],
        units: item.units || [],
      }));

      const payCart =
        (data.trans || []).map((t, i) => ({
          key: `p-${i}`,
          type: t.to_acc_name,
          from_acc_id: t.to_acc_id,
          from_acc_name: t.to_acc_name,
          acc_type_id: "cash", // or detect
          tran_amount: Number(t.tran_amount),
          cheque_number: t.cheque_number || "",
          date: t.date || null,
        }));
      
      const payServiceCart =
        (data.otherServiceTrans || []).map((s, i) => ({
          key: `s-${i}`,
          to_acc_id: s.to_acc_id,
          to_acc_name: s.to_acc_name,
          acc_type_id: s.acc_type_id,
          tran_amount: Number(s.tran_amount),
        }));

      return {
        customerInfo: {
          acc_id: data.customer_id,
          acc_name: data.acc_name,
          display_text: data.acc_name,
          institution_name: data.institution_name,
          contact_no: data.contact_no,
          address: data.address,
        },
        voucherNumber: data.voucher_no,
        selectedVoucherDetails: {
          sale_id: data.sale_id,
          voucher_no: data.sale_voucher_no || "", // if backend adds it later
          display_text: data.sale_voucher_no || "",
        },
        givenItemsList,
        returnItemsList,
        footerValues: {
          narration: data.narration,
          labour_cost: data.labour_cost,
          others_cost: data.others_cost,
          paid_amount: data.paid_amount,
          payCart: payCart,
          payServiceCart: payServiceCart,
        },
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchGivenItemsList = createAsyncThunk(
  "productReplace/fetchGivenItemsList",
  async ({ itemId, warehouseId }, { rejectWithValue }) => {
    try {
      const res = await getCurrentStockApi({ itemId, warehouseId });
      return res.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
export const fetchItemBySearch = createAsyncThunk(
  "productReplace/fetchItemBySearch",
  async (searchText = "", { rejectWithValue }) => {
    try {
      const res = await getItemBySearchApi({ search: searchText });
      return res.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCustomerSpecialPrice = createAsyncThunk(
  "productReplace/fetchCustomerSpecialPrice",
  async ({ itemId, accountId }, { rejectWithValue }) => {
    try {
      const res = await getCustomerSpecialPriceApi({
        item_id: itemId,
        account_id: accountId,
      });
      const data = res.data;
      if (data && data.length > 0) {
        return data[0].price; // Special price
      } else {
        return null; // No special price found
      }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);
